import type { DWHConfig } from '@media-platform/dwh-logger/build/types';
import { SLIDER_CLASS_NAMES } from '@media-ui/slider/src/components/Slider/Slider.vars';
import { logElementWithNewURL, getVirtualURL, hitVirtualGallery, updateDWHBaseParams, } from '../dwhLogger';
import { IVirtualGalleryOptions } from './virtualGallery.types';
import { IGalleryItemExtended } from 'Src/store/context/types';
export enum EVENT_NAMES {
    OPEN = 'OPEN',
    CHANGE = 'CHANGE',
    SPLASH = 'SPLASH',
    CLOSE = 'CLOSE'
}
interface IGalleryEventsConfig<T extends IGalleryItemExtended = IGalleryItemExtended> {
    initialItems: T[];
    initialPageURL: string;
}
const hitSlideChangeEvents = ({ pageURL, photo, gallery, }: {
    pageURL: string;
    photo: string;
    asyncTarget: DWHConfig['asyncTarget'];
    gallery?: number;
}): void => {
    const params: IVirtualGalleryOptions = gallery
        ? {
            type: 'slide',
            photo,
            gallery,
        }
        : {
            type: 'single',
            photo,
        };
    logElementWithNewURL(document.querySelector(GalleryEventsDispatcher.ACTIVE_SLIDE_SELECTOR), getVirtualURL(pageURL, params), true // Не вызываем page_open, вызовем вручную в hitVirtualGallery
    );
    hitVirtualGallery(pageURL, params);
};
const hitGalleryCloseEvents = (pageURL: string): void => {
    // Обновляем параметры и отправляем page_open, но ничего не инициализируем
    const params: IVirtualGalleryOptions = { type: 'finish' };
    updateDWHBaseParams({ url: window.location.href });
    hitVirtualGallery(pageURL, params);
};
const hitSplashEvents = (pageURL: string): void => {
    const params: IVirtualGalleryOptions = { type: 'splash' };
    logElementWithNewURL(document.querySelector(GalleryEventsDispatcher.ACTIVE_SLIDE_SELECTOR), getVirtualURL(pageURL, params), true // Не вызываем page_open, вызовем вручную в hitVirtualGallery
    );
    hitVirtualGallery(pageURL, params);
};
/**
 * Создает объект для быстрого получения ID галереи по ID изображения. Так как в одной статье может быть несколько галерей
 *
 * @param items Массив изображений галереи
 * @returns Объект, где ключи — ID изображения, а значение — ID галереи
 */
const createInidicies = (items: IGalleryItemExtended[]) => (items || []).reduce<Partial<Record<string, number>>>((acc, item: IGalleryItemExtended, index) => {
    acc[item.id] = item.galleryId || index;
    return acc;
}, {});
export default class GalleryEventsDispatcher<T extends IGalleryItemExtended = IGalleryItemExtended> {
    private items: T[];
    private pageURL: string;
    private isLoadedGallery?: boolean;
    static ACTIVE_SLIDE_SELECTOR = `[data-qa="Overlay"] .${SLIDER_CLASS_NAMES.ACTIVE}`;
    static instance: GalleryEventsDispatcher | undefined;
    constructor(config: IGalleryEventsConfig<T>) {
        this.items = config.initialItems;
        this.pageURL = config.initialPageURL;
        if (!GalleryEventsDispatcher.instance) {
            GalleryEventsDispatcher.instance = this;
        }
        return GalleryEventsDispatcher.instance as GalleryEventsDispatcher<T>;
    }
    static getInstance() {
        return GalleryEventsDispatcher.instance;
    }
    updateSlides(nextItems: T[]) {
        this.items = nextItems;
        return this;
    }
    updatePageURL(nextURL: string) {
        this.pageURL = nextURL;
        return this;
    }
    updateIsLoadedGallery(isLoadedGallery: boolean) {
        this.isLoadedGallery = isLoadedGallery;
        return this;
    }
    dispatch(event: EVENT_NAMES.SPLASH | EVENT_NAMES.CLOSE): void;
    dispatch(event: EVENT_NAMES.OPEN, data: Record<'slideID', string>): void;
    dispatch(event: EVENT_NAMES.CHANGE, data: Record<'slideIndex', number>): void;
    dispatch(event: EVENT_NAMES, data?: {
        slideIndex?: number;
        slideID?: string;
    }) {
        const asyncTarget = this.isLoadedGallery ? 'gallery_last_slide' : 'gallery';
        switch (event) {
            case EVENT_NAMES.OPEN: {
                if (data && data.slideID) {
                    hitSlideChangeEvents({
                        pageURL: this.pageURL,
                        photo: data.slideID,
                        asyncTarget,
                        gallery: createInidicies(this.items)[data.slideID],
                    });
                }
                break;
            }
            case EVENT_NAMES.CHANGE: {
                if (data && data.slideIndex) {
                    hitSlideChangeEvents({
                        pageURL: this.pageURL,
                        photo: this.items[data.slideIndex].id,
                        gallery: this.items[data.slideIndex].galleryId,
                        asyncTarget,
                    });
                }
                break;
            }
            case EVENT_NAMES.SPLASH: {
                hitSplashEvents(this.pageURL);
                break;
            }
            case EVENT_NAMES.CLOSE: {
                hitGalleryCloseEvents(this.pageURL);
                break;
            }
            default:
                break;
        }
        return this;
    }
}
